import { useTranslation } from 'react-i18next';

import { UsageMeter } from './UsageMeter.tsx';
import Tooltip from '../../core/components/Tooltip.tsx';
import { useChatContextUsage, useChatUsage } from '../hooks/useChatUsage.ts';

const statusKey = (percent: number, warning: string, full: string) =>
    percent >= 100 ? full : percent >= 80 ? warning : null;

const UsageSection = ({
    label,
    percent,
    line,
    status,
}: {
    label: string;
    percent: number;
    line: string;
    status: string | null;
}) => {
    const { t } = useTranslation('chat');
    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex justify-between gap-3 text-sm">
                <span className="font-medium text-content-strong">{label}</span>
                <span className="text-content-medium">{t('sidebar.usageTooltip.percent', { value: percent })}</span>
            </div>
            <UsageMeter percent={percent} />
            <div className="flex flex-col gap-0.5 text-xs text-content-medium">
                <span>{line}</span>
                {status && <span>{status}</span>}
            </div>
        </div>
    );
};

// the monthly budget is kept undisclosed: percent only, both in the bar and in the tooltip
export const ChatUsageIndicator = ({ conversationId }: { conversationId: string | null }) => {
    const { t } = useTranslation('chat');
    const { percent: monthPercent, resetDate } = useChatUsage();
    const chatPercent = useChatContextUsage(conversationId);

    const monthStatus =
        statusKey(monthPercent, 'sidebar.usageTooltip.monthWarning', 'sidebar.usageTooltip.monthFull') ||
        'sidebar.usageTooltip.monthReset';
    const chatStatus =
        chatPercent === null
            ? null
            : statusKey(chatPercent, 'sidebar.usageTooltip.chatWarning', 'sidebar.usageTooltip.chatFull');

    const tooltip = (
        <div className="flex flex-col">
            {chatPercent !== null && (
                <>
                    <UsageSection
                        label={t('sidebar.usageTooltip.chat')}
                        percent={chatPercent}
                        line={t('sidebar.usageTooltip.chatLine')}
                        status={chatStatus && t(chatStatus)}
                    />
                    <div className="my-3 border-0 border-t border-solid border-line-weak" />
                </>
            )}
            <UsageSection
                label={t('sidebar.usageTooltip.month')}
                percent={monthPercent}
                line={t('sidebar.usageTooltip.monthLine')}
                status={t(monthStatus, { date: resetDate })}
            />
        </div>
    );

    return (
        <Tooltip
            content={tooltip}
            side="top"
            align="start"
            sideOffset={6}
            className="w-72 max-w-none rounded-lg border border-solid border-line-weak bg-surface-weak p-4 text-content-strong shadow-md backdrop-blur-none"
        >
            <div
                tabIndex={0}
                aria-label={t('sidebar.usageTooltip.aria', { chat: chatPercent ?? 0, month: monthPercent })}
                className="flex items-center gap-3 border-0 border-t border-solid border-line-weak px-4 py-3 outline-none"
            >
                <span className="flex-none text-xs text-content-weak">{t('sidebar.usage')}</span>
                <UsageMeter percent={monthPercent} className="flex-1" />
            </div>
        </Tooltip>
    );
};
